/**
 * Hooks optimizados con React Query para Dashboards
 *
 * @description Centraliza el data fetching de dashboards con caching
 * automático y manejo de errores
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import {
  getAdminDashboardKpis,
  getTransactionReport,
  userService,
  type AdminDashboardKpis,
  type TransactionReport,
} from '@/services';

/**
 * Hook para obtener KPIs del dashboard de admin
 *
 * @description Caché de 5 minutos - KPIs cambian relativamente lento
 */
export function useAdminDashboardKpis() {
  return useQuery<AdminDashboardKpis, Error>({
    queryKey: queryKeys.dashboard.admin(),
    queryFn: async () => {
      return await getAdminDashboardKpis();
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    // Usa retry: 1 del default global
  });
}

/**
 * Hook para obtener reporte de transacciones
 *
 * @param limit Número de transacciones a obtener
 * @param page Página actual
 * @description Caché de 2 minutos - Transacciones cambian frecuentemente
 */
export function useTransactionReport(limit: number = 10, page: number = 1) {
  return useQuery<TransactionReport, Error>({
    queryKey: queryKeys.transactions.list({ limite: limit, pagina: page }),
    queryFn: async () => {
      return await getTransactionReport({ limite: limit, pagina: page });
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    // Usa retry: 1 del default global
  });
}

/**
 * Hook para obtener estadísticas del dashboard de estudiante
 *
 * @description Caché de 3 minutos
 */
export function useStudentDashboardStats() {
  return useQuery({
    queryKey: queryKeys.dashboard.student(),
    queryFn: async () => {
      const [stats, orders] = await Promise.all([
        userService.getUserDashboardStats(),
        userService.getUserOrders(),
      ]);
      return { stats, orders };
    },
    staleTime: 3 * 60 * 1000, // 3 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para obtener estadísticas de usuario (apoderado)
 *
 * @description Caché de 3 minutos
 */
export function useUserDashboardStats() {
  return useQuery({
    queryKey: queryKeys.dashboard.apoderado(),
    queryFn: async () => {
      return await userService.getUserDashboardStats();
    },
    staleTime: 3 * 60 * 1000, // 3 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });
}
